import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import { useAuth } from '../../context/AuthContext';
import { brand, neutral } from '../../theme';
import Section from '../ui/Section';
import EmptyState from '../ui/EmptyState';
import RevisionsTab from '../RevisionsTab';
import ReviewPlayer from '../ProjectWorkspace/ReviewPlayer';
import RevisionDecisionDialog from '../ProjectWorkspace/RevisionDecisionDialog';
import { useRevisionFiles } from '../ProjectWorkspace/useRevisionFiles';
import { saveFile } from '../ProjectWorkspace/revisionMedia';
import { DecisionType, describeChangeRounds, isAbortError, isReadyRevision, readableError } from '../ProjectWorkspace/reviewModel';
import type { ReviewAnnotation } from '../ProjectWorkspace/types';
import { NoteLike, RevisionLike, buildRevisionVersions } from './workflow';
import { REVISION_FILE_LIMIT_COPY } from '../../config/uploadConfig';

interface DeliverTabProps {
  revisions: RevisionLike[];
  notes: NoteLike[];
  userRole: 'creator' | 'editor' | 'unauthorized';
  finished: boolean;
  revisionCount: number;
  /** The project's change-round allowance (Infinity = no limit). */
  revisionLimit: number;
  /**
   * The tab is on screen. The player (and its keyboard shortcuts) only
   * exists then, so a hidden tab can't play or take key presses.
   */
  active: boolean;
  fps: number;
  editorName?: string | null;
  onUpload: () => void;
  /** Rejects on failure, which keeps the decision dialog open with the reason. */
  onRevisionStatusChange: (revisionId: string, status: string, comments?: string) => Promise<void>;
  /** A short confirmation after a decision is saved ("v2 approved"). */
  onDecided?: (message: string) => void;
}

const NO_ORIGINAL = {};
const NO_NOTES: ReviewAnnotation[] = [];

/**
 * Delivering back to the creator: a player for every cut (loaded with
 * progress and a Cancel, because the file needs the viewer's sign-in), the
 * versions with their status and the creator's decision on each, and the
 * upload or decision actions for the person looking.
 */
const DeliverTab: React.FC<DeliverTabProps> = ({
  revisions,
  notes,
  userRole,
  finished,
  revisionCount,
  revisionLimit,
  active,
  fps,
  editorName,
  onUpload,
  onRevisionStatusChange,
  onDecided,
}) => {
  const { getToken } = useAuth();
  const files = useRevisionFiles(getToken);
  const { load: loadRevisionFile, pin: pinRevisionFile } = files;
  const versions = useMemo(() => buildRevisionVersions(revisions, notes), [revisions, notes]);
  const [chosenKey, setChosenKey] = useState<string | null>(null);
  const [playRequest, setPlayRequest] = useState<{ key: string; nonce: number } | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [decision, setDecision] = useState<{ type: DecisionType; revisionId: string } | null>(null);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const playerRef = useRef<HTMLDivElement | null>(null);
  const nonce = useRef(0);

  // Only the creator decides, only on a cut that is ready, and only while the
  // project is open (the server refuses the rest).
  const canDecide = userRole === 'creator' && !finished;
  const waiting = canDecide ? versions.find((entry) => isReadyRevision(entry.revision.status)) : undefined;
  const canUpload = userRole === 'editor' && !finished;
  // Start on the cut waiting for a decision (else the latest); keep the
  // viewer's own choice across refreshes while that version still exists.
  const defaultKey = waiting?.revision.id ?? versions[0]?.revision.id ?? null;
  const chosenIsValid = Boolean(chosenKey && versions.some((entry) => entry.revision.id === chosenKey));
  const selectedKey = chosenIsValid ? chosenKey : defaultKey;

  useEffect(() => {
    pinRevisionFile(selectedKey);
  }, [selectedKey, pinRevisionFile]);

  const handlePlay = (revisionId: string) => {
    nonce.current += 1;
    setChosenKey(revisionId);
    setPlayRequest({ key: revisionId, nonce: nonce.current });
    playerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const handleDownload = async (revision: RevisionLike, version: number) => {
    if (downloadingId) return;
    setDownloadingId(revision.id);
    setMediaError(null);
    try {
      const url = await loadRevisionFile(revision);
      saveFile(url, revision.filename || `v${version}.mp4`);
    } catch (error) {
      if (!isAbortError(error)) {
        setMediaError(readableError(error, 'This version couldn’t be downloaded. Please try again.'));
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const openDecision = (type: DecisionType, revisionId: string) => {
    setDecision({ type, revisionId });
    setDecisionOpen(true);
  };

  const decisionVersion = decision ? versions.find((entry) => entry.revision.id === decision.revisionId) : undefined;

  /** The same status change as before: the revision is accepted or sent back. */
  const handleConfirmDecision = async (comments: string) => {
    if (!decision) return;
    await onRevisionStatusChange(decision.revisionId, decision.type === 'approve' ? 'ACCEPTED' : 'REVISION_REQUESTED', comments);
    const label = decisionVersion ? `v${decisionVersion.version}` : 'The cut';
    setDecisionOpen(false);
    onDecided?.(decision.type === 'approve' ? `${label} approved` : `Changes requested on ${label}`);
  };

  const nextVersion = versions.length + 1;
  const rounds = describeChangeRounds(revisionCount, revisionLimit);

  const sectionAction = canUpload ? (
    <Button variant="outlined" size="small" startIcon={<CloudUploadOutlinedIcon />} onClick={onUpload}>
      Upload v{nextVersion}
    </Button>
  ) : waiting ? (
    <>
      <Button variant="outlined" size="small" onClick={() => openDecision('changes', waiting.revision.id)}>
        Request changes
      </Button>
      <Button variant="contained" size="small" startIcon={<CheckOutlinedIcon />} onClick={() => openDecision('approve', waiting.revision.id)}>
        Approve v{waiting.version}
      </Button>
    </>
  ) : undefined;

  const steps: Array<[string, string]> = [
    ['Upload', `Export the cut from your editing app as MP4 or MOV (${REVISION_FILE_LIMIT_COPY}) and upload it. Each upload becomes the next version.`],
    ['Review', 'Versions sent for review go to the creator, who watches them and approves them or requests changes.'],
    [
      'Changes',
      Number.isFinite(revisionLimit)
        ? `Each request for changes uses one of the plan’s ${revisionLimit} change rounds. The creator’s comments appear on the version.`
        : 'Each request for changes starts a new change round (this plan has no limit). The creator’s comments appear on the version.',
    ],
    ['Delivered', 'An approved version is the final video. The project then becomes read-only.'],
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px' }, gap: 2.5, alignItems: 'start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
        {mediaError && (
          <Alert severity="error" onClose={() => setMediaError(null)}>
            {mediaError}
          </Alert>
        )}

        {active && versions.length > 0 && (
          <Box ref={playerRef} sx={{ scrollMarginTop: 16 }}>
            <ReviewPlayer
              versions={versions}
              selectedKey={selectedKey}
              onSelect={setChosenKey}
              files={files}
              playRequest={playRequest}
              original={NO_ORIGINAL}
              notes={NO_NOTES}
              fps={fps}
              onDownload={(revision, version) => void handleDownload(revision, version)}
              downloadingId={downloadingId}
            />
          </Box>
        )}

        <Section
          title="Revisions"
          description={versions.length > 0 ? `Newest first · ${rounds}` : rounds}
          action={sectionAction}
          disablePadding
        >
          {versions.length === 0 ? (
            <EmptyState
              compact
              icon={<MovieOutlinedIcon />}
              title="No revisions yet"
              description={
                userRole === 'editor'
                  ? `Export your cut as MP4 or MOV (${REVISION_FILE_LIMIT_COPY}). Your first upload becomes v1.`
                  : 'The editor hasn’t uploaded a cut yet.'
              }
            />
          ) : (
            <RevisionsTab
              revisions={revisions}
              notes={notes}
              userRole={userRole}
              canDecide={canDecide}
              activeId={active ? selectedKey : null}
              downloadingId={downloadingId}
              onPlay={handlePlay}
              onDownload={(revision, version) => void handleDownload(revision, version)}
              onDecide={openDecision}
            />
          )}
        </Section>
      </Box>

      <Section title="How delivery works">
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
          {steps.map(([title, text], index) => (
            <Box component="li" key={title} sx={{ display: 'flex', gap: 1.25 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: neutral[100],
                  border: `1px solid ${brand.border}`,
                  fontSize: 11.5,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{title}</Typography>
                <Typography variant="body2" color="text.secondary">{text}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Section>

      <RevisionDecisionDialog
        open={decisionOpen && Boolean(decisionVersion)}
        type={decision?.type ?? 'approve'}
        version={decisionVersion?.version ?? 1}
        nextVersion={nextVersion}
        roundsUsed={revisionCount}
        roundsLimit={revisionLimit}
        editorName={editorName}
        onClose={() => setDecisionOpen(false)}
        onConfirm={handleConfirmDecision}
      />
    </Box>
  );
};

export default DeliverTab;
